import type { ThemeConfig } from "@/types/theme";

export const tailwindOrange: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-orange-700 dark:text-orange-400",
    secondary: "text-orange-900 dark:text-orange-500",
    accent: "text-amber-600 dark:text-amber-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-orange-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-orange-600 dark:bg-orange-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-orange-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-orange-600 dark:bg-orange-500",
    backgroundSecondaryColor: "bg-orange-800 dark:bg-orange-700",
    backgroundAccentColor: "bg-amber-500 dark:bg-amber-400",
    backgroundPrimarySubtle: "bg-orange-50 dark:bg-orange-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-orange-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-orange-700 dark:text-orange-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-orange-600 dark:text-orange-400",
    iconBackgroundFilled: "bg-orange-100 dark:bg-orange-950",
    iconBackgroundGradient: "bg-gradient-to-br from-orange-500 to-amber-500",
    iconBackgroundDefault: "bg-orange-50 dark:bg-orange-950/50",
    iconBorder: "border-orange-200 dark:border-orange-800",

    // Border classes
    borderDefault: "border-orange-200 dark:border-gray-700",
    borderLight: "border-orange-100 dark:border-gray-800",
    borderStrong: "border-orange-300 dark:border-gray-600",
    borderPrimary: "border-orange-700 dark:border-orange-500",
    borderSecondary: "border-orange-900 dark:border-orange-700",
    borderBottom: "border-b border-orange-200 dark:border-gray-700",
    borderAccent: "border-orange-600 dark:border-orange-400",

    // Badge classes
    badgeBackground: "bg-orange-100 dark:bg-orange-950",
    badgeText: "text-orange-700 dark:text-orange-300",

    // Shadow classes
    shadowPrimary: "shadow-orange-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-orange-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(249,115,22,0.30)]", // orange-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-orange-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-orange-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-orange-400 dark:bg-orange-600",
    textEmphasis: "text-indigo-600 dark:text-indigo-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-orange-600 to-orange-700",
    gradientSecondary: "bg-gradient-to-br from-orange-700 to-orange-900",
    gradientAccent: "bg-gradient-to-r from-orange-500 to-amber-500",
    gradientSubtle:
      "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant:
      "bg-gradient-to-r from-orange-600 via-amber-500 to-orange-600",
    gradientHero:
      "bg-gradient-to-br from-orange-600 via-amber-500 to-orange-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-orange-100 dark:bg-orange-950", // soft warn tint acceptable in orange theme
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
